import { Alert, Flex, Spin, Tag, Tooltip, Typography } from "antd";
import Chart from "react-apexcharts";
import { useMemo } from "react";
import useRScore from "../../hooks/useRScore";
import { classeDoRscore, corDaDimensao } from "./rscoreClassificacao";

const { Text, Title } = Typography;

const DIMENSION_LABELS = {
  atracao: "Atra\u00e7\u00e3o",
  conversao: "Convers\u00e3o",
  rentabilidade: "Rentabilidade",
  engajamento: "Engajamento",
  equilibrio: "Equil\u00edbrio",
  evolucao: "Evolu\u00e7\u00e3o",
};

const DIMENSION_ORDER = [
  "atracao",
  "conversao",
  "rentabilidade",
  "engajamento",
  "equilibrio",
  "evolucao",
];

const DIMENSION_WEIGHTS = {
  atracao: "10%",
  conversao: "25%",
  rentabilidade: "25%",
  engajamento: "20%",
  equilibrio: "15%",
  evolucao: "5%",
};

const DIMENSION_TOOLTIPS = {
  atracao:
    "Mede o crescimento do fluxo de visitantes em comparação ao período anterior (mesmo período do ano anterior ou período imediatamente anterior). Para melhorar: invista em vitrinismo, divulgação externa e ações de atração.",
  conversao:
    "Proporção de visitantes que realizam uma compra, comparada ao benchmark de referência. Para melhorar: capacite a equipe de abordagem, melhore a exposição de produtos e reduza a fricção no ponto de venda.",
  rentabilidade:
    "Faturamento por m² mensualizado, comparado ao benchmark de referência. Para melhorar: aumente o ticket médio, otimize o mix de produtos e reveja a disposição do espaço da loja.",
  engajamento:
    "Combina a atenção média dos clientes e a receita gerada por minuto de atenção. Para melhorar: crie experiências que aumentem o tempo de visita — demonstrações, consultoria e atendimento personalizado.",
  equilibrio:
    "Mede como o faturamento está distribuído entre as diferentes áreas da loja. Lojas equilibradas não dependem excessivamente de uma única zona. Para melhorar: desenvolva e promova as áreas com menor contribuição.",
  evolucao:
    "Mede o impacto médio das ações e eventos cadastrados na loja, comparando faturamento e atenção nos 5 dias antes e após cada evento. Requer ao menos 2 ações com janela pós-evento completa no período.",
};

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function DimensionBar({ label, score, isNull, weight, dimKey }) {
  const displayScore = score ?? 0;
  const barColor = corDaDimensao(displayScore);
  const tooltipText = DIMENSION_TOOLTIPS[dimKey];

  return (
    <div className="rscore-dim-row">
      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
        <Flex align="center" gap={6}>
          <Tooltip
            title={tooltipText}
            placement="left"
            overlayStyle={{ maxWidth: 300 }}>
            <Text
              style={{
                fontSize: 13,
                cursor: "help",
                borderBottom: "1px dashed #94a3b8",
              }}>
              {label}
            </Text>
          </Tooltip>
          {isNull && (
            <Tag
              color="default"
              style={{ fontSize: 11, lineHeight: "16px", padding: "0 5px" }}>
              s/ dado
            </Tag>
          )}
        </Flex>
        <Flex align="center" gap={8}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {isNull ? "peso 0%" : `peso ${weight}`}
          </Text>
          <Text strong style={{ fontSize: 14, width: 44, textAlign: "right" }}>
            {isNull ? "--" : `${percentFormatter.format(displayScore)}`}
          </Text>
        </Flex>
      </Flex>
      <div className="rscore-dim-bar-track">
        <div
          className="rscore-dim-bar-fill"
          style={{
            width: `${isNull ? 0 : displayScore}%`,
            background: isNull ? "#e5e7eb" : barColor,
          }}
        />
      </div>
    </div>
  );
}

export default function RScorePanel({
  branchId,
  branchName,
  startDate,
  endDate,
}) {
  const { data, isLoading, isError } = useRScore(
    branchId,
    startDate,
    endDate,
    Boolean(branchId && startDate && endDate)
  );

  const radarSeries = useMemo(() => {
    if (!data?.scores) return [];
    return [
      {
        name: "rSCORE",
        data: DIMENSION_ORDER.map((dim) => {
          const v = data.scores[dim];
          return v === null || v === undefined ? 0 : Math.round(v * 10) / 10;
        }),
      },
    ];
  }, [data]);

  const radarOptions = useMemo(
    () => ({
      chart: {
        type: "radar",
        toolbar: { show: false },
        animations: { enabled: true, speed: 400 },
      },
      xaxis: {
        categories: DIMENSION_ORDER.map((d) => DIMENSION_LABELS[d]),
        labels: {
          style: { fontSize: "12px", colors: Array(6).fill("#475569") },
        },
      },
      yaxis: { show: false, min: 0, max: 100, tickAmount: 5 },
      fill: { colors: ["#3b82f6"], opacity: 0.18 },
      stroke: { width: 2, colors: ["#3b82f6"] },
      markers: {
        size: 4,
        colors: ["#3b82f6"],
        strokeColors: "#fff",
        strokeWidth: 2,
      },
      plotOptions: {
        radar: {
          polygons: {
            strokeColors: "#e9eef5",
            fill: { colors: ["#fff", "#fff"] },
          },
        },
      },
      tooltip: {
        y: {
          formatter: (v) => `${percentFormatter.format(v)} / 100`,
        },
      },
      legend: { show: false },
    }),
    []
  );

  const classConfig = classeDoRscore(data?.classificacao);

  if (isLoading) {
    return (
      <div className="rscore-panel rscore-panel-loading">
        <Spin size="default" />
        <Text type="secondary" style={{ marginLeft: 10 }}>
          Calculando rSCORE para {branchName}...
        </Text>
      </div>
    );
  }

  if (isError || (data && data.rscore_total === null)) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`rSCORE indispon\u00edvel para ${branchName}`}
        description={
          "Dados insuficientes para calcular o \u00edndice no per\u00edodo selecionado."
        }
        style={{ borderRadius: 10 }}
      />
    );
  }

  if (!data) return null;

  const nullDims = new Set(data.null_dims || []);

  return (
    <div className="rscore-panel">
      {/* Cabeçalho */}
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
        <Flex vertical gap={4}>
          <Title level={5} style={{ margin: 0 }}>
            rSCORE &mdash; {branchName}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {"Índice composto de performance"}
          </Text>
        </Flex>
        <Flex align="center" gap={12}>
          <div
            className="rscore-total-badge"
            style={{
              borderColor: classConfig.border,
              background: classConfig.bg,
            }}>
            <span
              className="rscore-total-number"
              style={{ color: classConfig.color }}>
              {percentFormatter.format(data.rscore_total)}
            </span>
            <span
              className="rscore-total-label"
              style={{ color: classConfig.color }}>
              / 100
            </span>
          </div>
          <Tag
            color={classConfig.tag}
            style={{ fontSize: 13, padding: "3px 10px", borderRadius: 8 }}>
            {data.classificacao?.charAt(0).toUpperCase() +
              data.classificacao?.slice(1)}
          </Tag>
        </Flex>
      </Flex>

      {nullDims.size > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ borderRadius: 8, padding: "6px 12px" }}
          message={
            <Text style={{ fontSize: 12 }}>
              {nullDims.size === 1
                ? "1 dimens\u00e3o sem"
                : `${nullDims.size} dimens\u00f5es sem`}{" "}
              {"dados suficientes. Peso zerado para essa(s) dimensão(ões)."}
            </Text>
          }
        />
      )}

      {/* Corpo: radar + barras */}
      <Flex gap={24} wrap="wrap" align="flex-start">
        {/* Radar */}
        <div className="rscore-radar-wrapper">
          <Chart
            options={radarOptions}
            series={radarSeries}
            type="radar"
            height={280}
            width={300}
          />
        </div>

        {/* Barras de dimensão */}
        <Flex vertical gap={10} style={{ flex: 1, minWidth: 240 }}>
          {DIMENSION_ORDER.map((dim) => (
            <DimensionBar
              key={dim}
              dimKey={dim}
              label={DIMENSION_LABELS[dim]}
              score={data.scores[dim]}
              isNull={nullDims.has(dim)}
              weight={DIMENSION_WEIGHTS[dim]}
            />
          ))}
        </Flex>
      </Flex>
    </div>
  );
}
